import React, { useState } from 'react';
import Layout from '../Layout/Layout.jsx';
import styles from '../Common/Common.module.css';
import successStyles from './SuccessStep.module.css';
import { testimonyApi } from '../../services/api.js';

const SuccessStep = ({ onReset, onViewMyVideos, completedStages = [], userToken }) => {
  const [downloadingStage, setDownloadingStage] = useState(null);
  const [downloadError, setDownloadError] = useState('');

  const handleDownload = async (stage) => {
    setDownloadingStage(stage.stage);
    setDownloadError('');
    try {
      const data = await testimonyApi.getMyDownloadUrl(stage.testimonyId, stage.stage, userToken);
      const link = document.createElement('a');
      link.href = data.downloadUrl;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setDownloadError(err.message || 'Failed to generate download link. Please try again.');
    } finally {
      setDownloadingStage(null);
    }
  };

  const hasVideos = completedStages.length > 0 && completedStages.some(s => s.videoUrl);

  return (
    <Layout>
      <div className={styles.container}>
        <div className={successStyles.card}>
        {/* Navy banner with confetti */}
        <div className={successStyles['success-banner']}>
          {/* Top divider shape - upward chevron */}
          <div className={successStyles['banner-divider-top']}>
            <svg viewBox="0 0 1200 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="0,60 600,0 1200,60" fill="#003875" />
            </svg>
          </div>

          {/* Confetti decorations */}
          <div className={successStyles['confetti-left']}>
            <svg viewBox="0 0 20 20" fill="#C8102E">
              <polygon points="10,0 13,7 20,8 15,13 16,20 10,16 4,20 5,13 0,8 7,7" />
            </svg>
          </div>
          <div className={successStyles['confetti-right']}>
            <svg viewBox="0 0 20 20" fill="#C8102E">
              <polygon points="10,0 13,7 20,8 15,13 16,20 10,16 4,20 5,13 0,8 7,7" />
            </svg>
          </div>
          <div className={successStyles['confetti-top-left']}>
            <div className={successStyles.confetti}></div>
          </div>
          <div className={successStyles['confetti-top-right']}>
            <div className={successStyles.confetti}></div>
          </div>
          <div className={successStyles['confetti-bottom-left']}>
            <div className={successStyles.confetti}></div>
          </div>
          <div className={successStyles['confetti-bottom-right']}>
            <div className={successStyles.confetti}></div>
          </div>

          {/* Star icon */}
          <div className={successStyles['star-icon']}>
            <svg viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1">
              <polygon points="12,2 15,9 22,10 17,15 18,22 12,18 6,22 7,15 2,10 9,9" />
            </svg>
          </div>

          <h1 className={successStyles['banner-title']}>YOUR VIDEO<br/>HAS BEEN SUBMITTED</h1>
          <p className={successStyles['banner-subtitle']}>Thank You</p>
          <p className={successStyles['banner-text']}>We deeply appreciate you sharing your story<br/>with Folds of Honor</p>

          {/* Diagonal divider shape */}
          <div className={successStyles['banner-divider']}>
            <svg viewBox="0 0 1200 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="0,0 1200,0 600,60" fill="#003875" />
            </svg>
          </div>
        </div>

        {/* Info box */}
        <div className={successStyles['info-box']}>
          <h3>What happens next?</h3>
          <p>
            Your video will be reviewed by our team and may be featured on our website and social media channels to inspire others and showcase the impact of Folds of Honor scholarships.
          </p>
        </div>

        {/* Download section */}
        {hasVideos && (
          <div className={successStyles['download-section']}>
            <h3 className={successStyles['download-title']}>Download Your Videos</h3>
            {downloadError && (
              <p className={successStyles['download-error']}>{downloadError}</p>
            )}
            <div className={successStyles['download-list']}>
              {completedStages.filter(s => s.videoUrl).map((stage) => (
                <button
                  key={stage.stage}
                  onClick={() => handleDownload(stage)}
                  disabled={downloadingStage === stage.stage}
                  className={successStyles['download-btn']}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7,10 12,15 17,10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {downloadingStage === stage.stage ? 'Preparing...' : `Stage ${stage.stage}: ${stage.stageTitle}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className={successStyles['button-group']}>
          {onViewMyVideos && (
            <button
              className={`${styles.button} ${styles['button-secondary']} ${successStyles['return-button']}`}
              onClick={onViewMyVideos}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              View My Submissions
            </button>
          )}
          {onReset && (
            <button
              className={`${styles.button} ${successStyles['record-button']}`}
              onClick={onReset}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              Record Another Video
            </button>
          )}
          <a
            href="https://foldsofhonor.org"
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.button} ${styles['button-secondary']} ${successStyles['return-button']}`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            Return to Folds of Honor
          </a>
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default SuccessStep;
